'use client';

import {
  autoUpdate,
  flip,
  FloatingPortal,
  offset,
  safePolygon,
  shift,
  size,
  useClick,
  useDismiss,
  useFloating,
  useFocus,
  useHover,
  useInteractions,
  useRole,
  useTransitionStyles,
  type Placement,
} from '@floating-ui/react';
import React, { useState } from 'react';

import { classNames } from '@/shared/lib/classNames/classNames';

interface FloatingTooltipProps {
  label: React.ReactNode;
  tooltip: React.ReactNode;
  className?: string;
  tooltipClassName?: string;
  placement?: Placement;
  /** Открывать по клику (по умолчанию hover + focus) */
  trigger?: 'hover' | 'click';
}

const ARROW_HEIGHT = 8;
const GAP = 4;

/**
 * Тултип на базе @floating-ui/react.
 * - Рендерится в портале → не обрезается overflow:hidden родителей.
 * - Стрелка через <FloatingArrow/>, корректно реагирует на flip/shift.
 * - Поддерживает hover, focus, click, escape, outside-click.
 */
export const FloatingTooltip: React.FC<FloatingTooltipProps> = ({
  label,
  tooltip,
  className,
  tooltipClassName,
  placement = 'top',
  trigger = 'hover',
}) => {
  const [open, setOpen] = useState(false);

  const { refs, floatingStyles, context } = useFloating({
    open,
    onOpenChange: setOpen,
    placement,
    whileElementsMounted: autoUpdate,
    middleware: [
      offset(ARROW_HEIGHT + GAP),
      flip({ padding: 8 }),
      shift({ padding: 8 }),
      size({
        padding: 8,
        apply({ availableWidth, elements }) {
          Object.assign(elements.floating.style, {
            maxWidth: `${Math.min(320, availableWidth)}px`,
          });
        },
      }),
    ],
  });

  const hover = useHover(context, {
    enabled: trigger === 'hover',
    move: false,
    handleClose: safePolygon(),
  });
  const focus = useFocus(context, { enabled: trigger === 'hover' });
  const click = useClick(context, { enabled: trigger === 'click' });
  const dismiss = useDismiss(context);
  const role = useRole(context, { role: 'tooltip' });

  const { getReferenceProps, getFloatingProps } = useInteractions([
    hover,
    focus,
    click,
    dismiss,
    role,
  ]);

  const { isMounted, styles: transitionStyles } = useTransitionStyles(context, {
    duration: 150,
    initial: { opacity: 0, transform: 'scale(0.96)' },
  });

  return (
    <>
      <span
        ref={refs.setReference}
        {...getReferenceProps()}
        className={classNames({
          cls: 'relative inline-flex items-center justify-center',
          additional: [className],
        })}
      >
        {label}
      </span>

      {isMounted && (
        <FloatingPortal>
          <div
            ref={refs.setFloating}
            style={floatingStyles}
            {...getFloatingProps()}
            className="z-50"
          >
            <div
              style={transitionStyles}
              className={classNames({
                cls: 'bg-surface-base-tertiary text-text-base-primary relative rounded-[8px] px-3 py-2 text-[14px] leading-[20px] tracking-[-0.16px] shadow-[0_5px_10px_0_rgba(25,32,37,0.24)]',
                additional: [tooltipClassName],
              })}
            >
              {tooltip}
            </div>
          </div>
        </FloatingPortal>
      )}
    </>
  );
};
